import React from 'react';
import Stack from '@mui/material/Stack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import {SxType} from "@baseTypes/sx/sxTypes";
import IconButtonCustom from "@ui/buttons/iconButtonCustom/IconButtonCustom";
import {useTranslation} from "react-i18next";
import {ITaskExecutionSingleVoyage} from "@containers/singleVoyage/tasks/types";
import {
    useChangeExecutionStatusSingleVoyageQuery
} from "@containers/singleVoyage/tasks/queryHooks/useTasksSingleVoyageQuery";

const decision = {
    padding: '2px',
    borderRadius: '4px',
    color: 'text.secondary',
    transition: 'color .15s, background-color .15s',
}

const sx: SxType = {
    root: {
        flexShrink: 0,
    },
    done: {
        ...decision,
        '&:hover': {backgroundColor: 'action.hover', color: 'success.main'},
    },
    failed: {
        ...decision,
        '&:hover': {backgroundColor: 'action.hover', color: 'error.main'},
    },
    icon: {
        width: '15px',
        height: '15px',
    },
}

interface IProps {
    taskId: number
    accountId: string | number
    voyageId: number
    execution: ITaskExecutionSingleVoyage
    closeModalFn?: () => void
    closePopoverFn?: () => void
}

/**
 * Closing the work at a single stop: a tick and a cross on the row itself.
 *
 * Two outcomes, two shapes - which is the whole vocabulary needed, so they carry
 * no colour of their own until you hover one. This used to be a menu behind a
 * three-dot trigger with two full-width worded buttons inside it: three presses
 * and a popover for the most common action on the screen.
 *
 * Deliberately does not consult the geofence or the visit log: under jamming the
 * detection is lost, and the driver must be able to close the work regardless.
 */
const ChangeTaskStatusSingleVoyage = ({
                                          taskId,
                                          accountId,
                                          voyageId,
                                          execution,
                                          closeModalFn,
                                          closePopoverFn,
                                      }: IProps) => {

    const {t} = useTranslation()

    const {
        isPending,
        mutate: save
    } = useChangeExecutionStatusSingleVoyageQuery({
        onSuccess: () => {
            closeModalFn && closeModalFn()
            closePopoverFn && closePopoverFn()
        }
    })

    const onChange = (status: 'COMPLETED' | 'CANCELLED') => {
        save({
            accountId: Number(accountId),
            voyageId,
            taskId,
            executionId: execution.id,
            status,
        })
    }

    // A closed stop has nothing left to offer: the same status is a no-op and
    // anything else is a conflict.
    if (execution.status !== 'PLANNED') {
        return null
    }

    if (isPending) {
        return <CircularProgress size={14} sx={sx.root}/>
    }

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            gap={'1px'}
            sx={sx.root}
        >
            <IconButtonCustom
                variant={'empty'}
                sx={sx.done}
                tooltip={t('singleVoyage.markExecutionCompleted')}
                onClick={() => onChange('COMPLETED')}
            >
                <CheckIcon sx={sx.icon}/>
            </IconButtonCustom>

            <IconButtonCustom
                variant={'empty'}
                sx={sx.failed}
                tooltip={t('singleVoyage.markExecutionCancelled')}
                onClick={() => onChange('CANCELLED')}
            >
                <CloseIcon sx={sx.icon}/>
            </IconButtonCustom>
        </Stack>
    );
};

export default ChangeTaskStatusSingleVoyage;
